import React from 'react';
import { EventSnapshot } from '../../../api/types';
import { getCountryFlagUrl } from '../../../utils/flagUtils';
import { useTranslation } from '../../../i18n';
import { translateMarketFilterLabel } from '../../../utils/marketTranslations';

interface LiveMatchListProps {
  title: string;
  events: EventSnapshot[];
  activeSport: string;
  setActiveSport: (s: string) => void;
  sportsTab: { id: string; name: string; emoji?: string }[];
  liveMarket1: string | number;
  setLiveMarket1: (m: string | number) => void;
  liveMarket2: string | number;
  setLiveMarket2: (m: string | number) => void;
  liveMarket3: string | number;
  setLiveMarket3: (m: string | number) => void;
  liveMarket4: string | number;
  setLiveMarket4: (m: string | number) => void;
  liveMarket5: string | number;
  setLiveMarket5: (m: string | number) => void;
  openDropdown: string | null;
  setOpenDropdown: (val: string | null | ((prev: string | null) => string | null)) => void;
  options: (string | number)[];
  renderMarketColHeader: (type: string | number, width: string) => React.ReactNode;
  renderMarketColCells: (ev: EventSnapshot, type: string | number, width: string, isUp?: boolean, isDown?: boolean) => React.ReactNode;
  handleEventClick: (ev: EventSnapshot) => void;
  formatClock: (ev: EventSnapshot) => string;
  onViewAll?: () => void;
  showViewAll?: boolean;
  hideSecondMarket?: boolean;
  activeStreams?: Set<string>;
  loadingMoreLive?: boolean;
  onLoadMoreLiveUpcoming?: () => void;
}

export const LiveMatchList: React.FC<LiveMatchListProps> = ({
  title,
  events,
  activeSport,
  setActiveSport,
  sportsTab,
  liveMarket1,
  setLiveMarket1,
  liveMarket2,
  setLiveMarket2,
  liveMarket3,
  setLiveMarket3,
  liveMarket4,
  setLiveMarket4,
  liveMarket5,
  setLiveMarket5,
  openDropdown,
  setOpenDropdown,
  options,
  renderMarketColHeader,
  renderMarketColCells,
  handleEventClick,
  formatClock,
  onViewAll,
  showViewAll = false,
  hideSecondMarket = false,
  activeStreams = new Set()
}) => {
  const { locale } = useTranslation();

  const { t } = useTranslation();
  if (events.length === 0 && sportsTab.length <= 1) return null;

  return (
    <div>
      <h2 className="text-[15px] font-bold text-gray-900 mb-2 px-1">{title}</h2>

      <div className="bg-white rounded shadow-sm border border-gray-200 relative z-20">

        {/* Sport Tabs */}
        <div className="flex bg-white px-2 pt-1 border-b border-gray-200 overflow-x-auto scrollbar-hide">
          {sportsTab.map((s) => {
            return (
              <button
                key={s.id}
                onClick={() => setActiveSport(s.id)}
                className={`px-5 py-3 text-[14px] font-bold transition border-b-[3px] -mb-[1px] whitespace-nowrap ${activeSport === s.id
                    ? 'border-[#cc0000] text-gray-900'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
              >
                {s.emoji ? <span className="mr-1.5 text-[16px] leading-none">{s.emoji}</span> : null}
                {s.name}
              </button>
            );
          })}
        </div>

        {/* Table Dropdowns Top */}
        {events.length > 0 && (
          <div className="px-5 py-2 flex justify-end bg-[#f7f8f9] border-b border-gray-200 sticky top-[56px] z-20">
            <div className="relative">
              <div onClick={() => setOpenDropdown(o => o === 'l1' ? null : 'l1')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket1, locale)}</span>
                <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'l1' && (
                <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setLiveMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{liveMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={liveMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={`relative${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>
              <div onClick={() => setOpenDropdown(o => o === 'l2' ? null : 'l2')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket2, locale)}</span>
                <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'l2' && (
                <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setLiveMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{liveMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={liveMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={`relative ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
              <div onClick={() => setOpenDropdown(o => o === 'l3' ? null : 'l3')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket3, locale)}</span>
                <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'l3' && (
                <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setLiveMarket3(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{liveMarket3 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={liveMarket3 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!hideSecondMarket && (
              <>
                <div className="relative hidden min-[1650px]:block">
                  <div onClick={() => setOpenDropdown(o => o === 'l4' ? null : 'l4')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                    <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket4, locale)}</span>
                    <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                  {openDropdown === 'l4' && (
                    <div className="absolute top-full right-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                      <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                      {options.map(opt => (
                        <button key={opt} onClick={() => { setLiveMarket4(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                          <div className="w-4">{liveMarket4 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                          <span className={liveMarket4 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative hidden min-[1750px]:block">
                  <div onClick={() => setOpenDropdown(o => o === `live5-${title}` ? null : `live5-${title}`)} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                    <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(liveMarket5, locale)}</span>
                    <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                  {openDropdown === `live5-${title}` && (
                    <div className="absolute top-full right-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                      <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Markt Wählen', locale)}</div>
                      {options.map(opt => (
                        <button key={opt} onClick={() => { setLiveMarket5(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                          <div className="w-4">{liveMarket5 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                          <span className={liveMarket5 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="w-[52px]" />
          </div>
        )}


        {/* Matches Grouped by Tournament */}
        {events.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-[14px]">
            Keine Live-Begegnungen in dieser Sportart verfügbar.
          </div>
        ) : (
          Object.entries(
            events.reduce((acc, ev) => {
              const t = ev.event.tournament_name || 'Andere';
              if (!acc[t]) acc[t] = [];
              acc[t].push(ev);
              return acc;
            }, {} as Record<string, EventSnapshot[]>)
          ).map(([tournament, eventsGroup]) => (
            <React.Fragment key={tournament}>
              <div className="px-5 py-1.5 bg-[#f7f8f9] border-t border-b border-gray-200 flex items-center justify-between mt-[-1px]">
                <div className="flex items-center gap-2">
                  {(() => { const flagUrl = getCountryFlagUrl(eventsGroup[0]?.event?.country_code); return flagUrl ? <img src={flagUrl} alt="" className="w-6 h-[18px] object-cover flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : null; })()}
                  <span className="text-[12px] font-bold text-gray-700">{tournament}</span>
                </div>
                <div className="flex items-center text-[13px] font-bold text-gray-400">
                  {renderMarketColHeader(liveMarket1, "w-[151px]")}
                  <div className={`flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>{renderMarketColHeader(liveMarket2, "w-[151px]")}</div>
                  <div className={`flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>{renderMarketColHeader(liveMarket3, "w-[151px]")}</div>
                  {!hideSecondMarket && <div className="hidden min-[1650px]:block flex-shrink-0">{renderMarketColHeader(liveMarket4, "w-[151px]")}</div>}
                  {!hideSecondMarket && <div className="hidden min-[1750px]:block flex-shrink-0">{renderMarketColHeader(liveMarket5, "w-[151px]")}</div>}
                  <div className="w-[52px]" />
                </div>
              </div>
              {eventsGroup.map((ev) => {
                const total = ev.markets?.reduce((a, m) => a + m.outcomes.length, 0) ?? 0;
                const clockStr = formatClock(ev) || "HZ";
                const homeScore = ev.status?.home_score ?? 0;
                const awayScore = ev.status?.away_score ?? 0;

                return (
                  <div key={ev.event_id} className="px-5 py-1 flex items-start hover:bg-gray-50 transition border-b border-gray-200 bg-white last:border-b-0">
                    <div className="w-[36px] flex-shrink-0 cursor-pointer pt-0.5 text-left border-r border-gray-100 pr-2" onClick={() => handleEventClick(ev)}>
                      <div className="text-[13px] font-bold text-gray-900">{clockStr}</div>
                      <div className="text-[9px] font-bold text-[#cc0000] tracking-widest mt-0.5">LIVE</div>
                    </div>

                    <div className="flex-1 min-w-0 flex items-center cursor-pointer border-r border-gray-100 pr-4 pl-2 relative" onClick={() => handleEventClick(ev)}>
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="flex items-center gap-2 min-w-0 h-[22px]">
                          {ev.event.home.crest_url ? <img src={ev.event.home.crest_url} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" /> : <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />}
                          <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
                        </div>
                        <div className="flex items-center gap-2 min-w-0 h-[22px]">
                          {ev.event.away.crest_url ? <img src={ev.event.away.crest_url} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" /> : <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />}
                          <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
                        </div>
                        {ev.event.sport_id === 'sr:sport:1' && ev.markets?.some(mk => (mk.market_id === '60' || mk.market_id === '6' || mk.market_id === '436' || mk.market_id === '61' || mk.market_id === '62' || mk.market_id === '119') && mk.status !== -1) && (
                          <div className="text-[11px] font-bold text-[#D41534] mt-0.5 pl-6">{t('market.firstHalf')}</div>
                        )}
                      </div>

                      {activeStreams.has(ev.event_id) && (
                        <div className="mx-3 flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}

                      <div className="flex flex-col justify-center flex-shrink-0 text-right">
                        <div className="h-[22px] flex items-center justify-end">
                          <span className="text-[15px] font-medium text-[#0088cc]">{homeScore}</span>
                        </div>
                        <div className="h-[22px] flex items-center justify-end">
                          <span className="text-[15px] font-medium text-[#0088cc]">{awayScore}</span>
                        </div>
                      </div>
                    </div>

                    {renderMarketColCells(ev, liveMarket1, "w-[151px]")}
                    <div className={`flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>
                      {renderMarketColCells(ev, liveMarket2, "w-[151px]")}
                    </div>
                    <div className={`flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
                      {renderMarketColCells(ev, liveMarket3, "w-[151px]")}
                    </div>
                    {!hideSecondMarket && (
                      <>
                        <div className="hidden min-[1650px]:block flex-shrink-0">
                          {renderMarketColCells(ev, liveMarket4, "w-[151px]")}
                        </div>
                        <div className="hidden min-[1750px]:block flex-shrink-0">
                          {renderMarketColCells(ev, liveMarket5, "w-[151px]")}
                        </div>
                      </>
                    )}

                    <button onClick={() => handleEventClick(ev)} className="w-[52px] flex items-start justify-end pt-2 text-[15px] font-medium text-gray-500 hover:text-gray-800 transition">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                      {total} ›
                    </button>
                  </div>
                );
              })}
            </React.Fragment>
          ))
        )}

        {showViewAll && onViewAll && (
          <div className="bg-[#f7f8f9] border-t border-gray-200 px-5 py-3 flex justify-end">
            <button onClick={onViewAll} className="text-[15px] font-bold text-gray-500 hover:text-gray-900 transition flex items-center">
              Alle <span className="text-lg leading-none ml-1">›</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
